<template>
  <div v-if="$apollo.loading">
    <spinner />
  </div>
  <form v-else v-scroll-to-error @submit.prevent="submitForm">
    <v-layout class="workshop-update-container">
      <v-flex xs12 sm10 md8 class="form-container">
        <v-layout wrap>
          <v-flex xs12>
            <div class="screen-title mb-32">
              <span>{{ adminText('workflowsAll.update_workshop_75af3eb', 'Update Workshop') }}</span>
            </div>
            <workshop-form
              :vs="$v"
              :initial-values="form"
              :halls="halls"
              :event="event"
              :feedback-total="feedbackTotal"
              @values="form = $event"
              @showFeedback="showFeedbackModal = true"
            />
            <main-button :text="adminText('workflowsAll.update_fb91e24', 'Update')" depressed @click="$emit('submit')" />
            <main-button
              :text="adminText('workflowsAll.delete_f6fdbe4', 'Delete')"
              color="error"
              flat
              @click.prevent="showDeleteDialog = true"
            />
            <main-button :text="adminText('workflowsAll.cancel_77dfd21', 'Cancel')" flat @click.prevent="goBack" />
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex xs12 sm4 class="workshop-details">
        <workshop-details :event-id="eventId" :workshop="workshop" />
      </v-flex>
      <delete-dialog
        :content="workflowLabel((adminText('workflowsAll.are_you_sure_you_want_to_delete_this_workshop_9aad8ee', 'Are you sure you want to delete this workshop? This operation cannot be undone.')))"
        :on-delete="() => deleteWorkshop()"
        :show.sync="showDeleteDialog"
        :title="adminText('workflowsAll.remove_workshop_05b5229', 'Remove workshop')"
      />
      <workshop-feedback-modal
        v-model="showFeedbackModal"
        :event-id="eventId"
        :workshop-id="workshopId"
        :workshop-name="workshop.name || ''"
      />
    </v-layout>
  </form>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { apolloClient } from '../../apollo'
import { GET_EVENT_WORKSHOP_AND_HALLS, GET_WORKSHOP_FEEDBACK } from '../../graphql/workshop.graphql.js'
import DeleteDialog from './../shared/dialogs/delete-dialog.vue'
import WorkshopForm from './workshop-form.vue'
import WorkshopDetails from './workshop-details.vue'
import WorkshopFeedbackModal from './workshop-feedback-modal.vue'
import MainButton from './../shared/main-button'
import Spinner from './../shared/spinner'
import { deleteWorkshop, updateWorkshop } from './../../http'
import { required, requiredIf, minValue } from 'vuelidate/lib/validators'
import { date, minDate, maxDate, minValueIf } from './../../validators'
import { submitGuard } from './../../utils'

export default {
  mixins: [WorkflowDisplay],
  name: 'update-workshop-container',
  components: {
    WorkshopForm,
    WorkshopDetails,
    WorkshopFeedbackModal,
    MainButton,
    Spinner,
    DeleteDialog,
  },
  props: {
    eventId: {
      default: null,
      type: String,
    },
    workshopId: {
      default: null,
      type: String,
    },
  },
  validations() {
    const component = this
    const event = this.event

    return {
      form: {
        name: { required },
        // the attendee app lays the programme out by hall, so conference events need one
        hallId: {
          required: requiredIf(() => Boolean(event.extraModules && event.extraModules.conferenceMobileApp)),
        },
        from: {
          required,
          date,
          minDate: minDate(() => new Date(event.from)),
          maxDate: maxDate(() => component.form.to || new Date(event.to)),
        },
        to: {
          required,
          date,
          minDate: minDate(() => component.form.from || new Date(event.from)),
          maxDate: maxDate(() => new Date(event.to)),
        },
        attendanceLimit: {
          required: requiredIf(values => !values.unlimitedAttendance),
          minValue: minValueIf(
            component.attending,
            () => component.form.unlimitedAttendance || false,
          ),
        },
      },
    }
  },
  data: () => ({
    showDeleteDialog: false,
    showFeedbackModal: false,
    // Declared rather than only assigned in the result handler below, which Vue 2 cannot
    // observe — and which left `:event` non-reactive for the form.
    event: {},
    workshop: {},
    form: { lecturers: [] },
    attending: 0,
    feedbackTotal: 0,
    halls: [],
  }),
  apollo: {
    getEventWorkshop: {
      query: GET_EVENT_WORKSHOP_AND_HALLS,
      variables() {
        return {
          eventId: this.eventId,
          workshopId: this.workshopId,
        }
      },
      manual: true,
      result({ loading, data }) {
        if (!loading) {
          const {
            getWorkshop: { event, ...workshop },
            getHalls,
          } = data
          const { attendees, attending, lecturers, ...w } = workshop
          this.workshop = workshop
          this.form = {
            ...w,
            lecturers,
          }
          this.halls = getHalls
          this.attending = attending
          this.event = event
          this.loadFeedbackTotal()
        }
      },
    },
  },
  methods: {
    /**
     * COR-1684: just the count behind the form's "See all the feedback" link. Fetched by
     * hand rather than declared in `apollo`, because a second declared query flips
     * `$apollo.loading` and the template tears the form down while it is in flight. The
     * modal runs its own query, and pages it.
     */
    async loadFeedbackTotal() {
      if (!(this.event.extraModules && this.event.extraModules.conferenceMobileApp)) return

      // A missing count must never block editing the workshop, so errors stay here.
      try {
        const { data } = await apolloClient.query({
          query: GET_WORKSHOP_FEEDBACK,
          variables: { eventId: this.eventId, workshopId: this.workshopId, page: 1, perPage: 1 },
        })

        if (data && data.getWorkshopFeedback) {
          this.feedbackTotal = data.getWorkshopFeedback.total
        }
      } catch (error) {
        console.error('Failed to load the workshop feedback count', error)
      }
    },
    async deleteWorkshop() {
      await deleteWorkshop(this.eventId, this.workshopId)
      this.goBack()
    },
    async submitForm() {
      await submitGuard(this, async () => {
        const { lecturers, __typename, ...values } = this.form
        const attendanceLimit = values.unlimitedAttendance ? 0 : values.attendanceLimit
        const data = { ...values, attendanceLimit }
        await updateWorkshop({
          eventId: this.eventId,
          workshopData: {
            id: this.workshopId,
            lecturerIds: lecturers.map(l => l.id),
            ...data,
          },
          refetchQueries: ['GetWorkshops'],
        })
        this.goBack()
      })
    },
  },
}
</script>

<style>
.form-container {
  margin-right: 30px;
}
.workshop-details {
  margin: 68px -30px 0 0;
}
@media (max-width: 800px) {
  .workshop-update-container {
    display: flex;
    flex-direction: column;
  }
  .form-container {
    margin-right: 0;
  }
  .email-shortcodes {
    margin: 32px 0;
  }
  .workshop-details {
    margin: 24px 0;
    width: 100%;
  }
}
</style>
